// Adapter file to map Semantic UI components to Ant Design components
import React from 'react'
import {
  Button as AntButton,
  Input as AntInput,
  Form as AntForm,
  Modal as AntModal,
  Select as AntSelect,
  Radio as AntRadio,
  Checkbox as AntCheckbox,
  Table as AntTable,
  Menu as AntMenu,
  Dropdown as AntDropdown,
  Card as AntCard,
  Row,
  Col,
  Space,
  Divider as AntDivider,
  Typography,
  Grid as AntGrid,
  List as AntList,
  Tabs as AntTabs,
  Popover as AntPopover,
  message,
  Progress as AntProgress,
  Tag as AntTag,
  Empty,
  Spin,
  Alert,
  Avatar as AntAvatar,
  Badge as AntBadge,
  Breadcrumb as AntBreadcrumb,
  DatePicker as AntDatePicker,
  Steps as AntSteps,
  Switch as AntSwitch,
  Upload as AntUpload,
  Tooltip as AntTooltip,
  Pagination as AntPagination,
} from 'antd'

// Type definitions
export type SemanticCOLORS =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'olive'
  | 'green'
  | 'teal'
  | 'blue'
  | 'violet'
  | 'purple'
  | 'pink'
  | 'brown'
  | 'grey'
  | 'black'
import * as AntIcons from '@ant-design/icons'

// Icon mapping
export const Icon = ({ name, ...props }: { name: string } & any) => {
  const iconMap: Record<string, React.ComponentType<any>> = {
    'arrow left': AntIcons.ArrowLeftOutlined,
    'arrow right': AntIcons.ArrowRightOutlined,
    check: AntIcons.CheckOutlined,
    checkmark: AntIcons.CheckOutlined,
    close: AntIcons.CloseOutlined,
    delete: AntIcons.DeleteOutlined,
    trash: AntIcons.DeleteOutlined,
    edit: AntIcons.EditOutlined,
    plus: AntIcons.PlusOutlined,
    'plus circle': AntIcons.PlusCircleOutlined,
    search: AntIcons.SearchOutlined,
    user: AntIcons.UserOutlined,
    upload: AntIcons.UploadOutlined,
    download: AntIcons.DownloadOutlined,
    file: AntIcons.FileOutlined,
    folder: AntIcons.FolderOutlined,
    setting: AntIcons.SettingOutlined,
    info: AntIcons.InfoCircleOutlined,
    warning: AntIcons.WarningOutlined,
    question: AntIcons.QuestionCircleOutlined,
    'check circle': AntIcons.CheckCircleOutlined,
    'times circle': AntIcons.CloseCircleOutlined,
    eye: AntIcons.EyeOutlined,
    'eye slash': AntIcons.EyeInvisibleOutlined,
    lock: AntIcons.LockOutlined,
    unlock: AntIcons.UnlockOutlined,
    copy: AntIcons.CopyOutlined,
    calendar: AntIcons.CalendarOutlined,
    clock: AntIcons.ClockCircleOutlined,
    mail: AntIcons.MailOutlined,
    phone: AntIcons.PhoneOutlined,
    home: AntIcons.HomeOutlined,
    dollar: AntIcons.DollarOutlined,
    percent: AntIcons.PercentageOutlined,
    tag: AntIcons.TagOutlined,
    tags: AntIcons.TagsOutlined,
    book: AntIcons.BookOutlined,
    video: AntIcons.VideoCameraOutlined,
    image: AntIcons.PictureOutlined,
    camera: AntIcons.CameraOutlined,
    cloud: AntIcons.CloudOutlined,
    'cloud upload': AntIcons.CloudUploadOutlined,
    'cloud download': AntIcons.CloudDownloadOutlined,
    'exclamation triangle': AntIcons.ExclamationOutlined,
    pencil: AntIcons.EditOutlined,
    redo: AntIcons.RedoOutlined,
    'copy outline': AntIcons.CopyOutlined,
    bold: AntIcons.BoldOutlined,
    italic: AntIcons.ItalicOutlined,
    strikethrough: AntIcons.StrikethroughOutlined,
    'unordered list': AntIcons.UnorderedListOutlined,
    'ordered list': AntIcons.OrderedListOutlined,
    indent: AntIcons.MenuUnfoldOutlined,
    outdent: AntIcons.MenuFoldOutlined,
    font: AntIcons.FontSizeOutlined,
    'paint brush': AntIcons.HighlightOutlined,
    'align left': AntIcons.AlignLeftOutlined,
    'align center': AntIcons.AlignCenterOutlined,
    'align right': AntIcons.AlignRightOutlined,
    'align justify': AntIcons.AlignLeftOutlined,
  }

  const IconComponent = iconMap[name] || AntIcons.QuestionOutlined
  return <IconComponent {...props} />
}

// Button adapter
export const Button = ({
  primary,
  secondary,
  basic,
  positive,
  negative,
  circular,
  fluid,
  size,
  loading,
  disabled,
  icon,
  content,
  children,
  ...props
}: any) => {
  const typeMap: Record<string, any> = {
    primary: 'primary',
    secondary: 'default',
    positive: 'primary',
    negative: 'danger',
  }

  const type = primary
    ? 'primary'
    : secondary
      ? 'default'
      : positive
        ? 'primary'
        : negative
          ? 'danger'
          : undefined
  const shape = circular ? 'round' : undefined
  const block = fluid

  return (
    <AntButton
      type={type}
      shape={shape}
      block={block}
      size={size}
      loading={loading}
      disabled={disabled || loading}
      {...props}
    >
      {icon && typeof icon === 'string' ? <Icon name={icon} /> : icon}
      {content || children}
    </AntButton>
  )
}

// Input adapter
export const Input = React.forwardRef(
  (
    {
      icon,
      iconPosition,
      placeholder,
      type,
      error,
      fluid,
      size,
      action,
      label,
      labelPosition,
      transparent,
      ...props
    }: any,
    ref
  ) => {
    const prefix = iconPosition === 'left' && icon ? <Icon name={icon} /> : undefined
    const suffix =
      (iconPosition === 'right' || !iconPosition) && icon ? <Icon name={icon} /> : undefined

    if (type === 'password') {
      return (
        <AntInput.Password
          ref={ref}
          placeholder={placeholder}
          size={size}
          prefix={prefix}
          {...props}
        />
      )
    }

    if (action) {
      return (
        <AntInput.Search
          ref={ref}
          placeholder={placeholder}
          size={size}
          prefix={prefix}
          enterButton={action === true ? true : action}
          {...props}
        />
      )
    }

    return (
      <AntInput
        ref={ref}
        placeholder={placeholder}
        size={size}
        prefix={prefix}
        suffix={suffix}
        {...props}
      />
    )
  }
)

// Form components
const BaseForm = AntForm
export const FormField = ({ label, error, children, width, required, ...props }: any) => {
  return (
    <AntForm.Item
      label={label}
      validateStatus={error ? 'error' : ''}
      help={error}
      required={required}
      {...props}
    >
      {children}
    </AntForm.Item>
  )
}
const FormWithSubcomponents = BaseForm as typeof BaseForm & {
  Field: typeof FormField
  Input: React.ComponentType<any>
}
FormWithSubcomponents.Field = FormField
FormWithSubcomponents.Input = (props: any) => (
  <FormField {...props}>
    <Input {...props} />
  </FormField>
)

export { FormWithSubcomponents as Form }

// Grid components
export const Grid = ({ columns, stackable, ...props }: any) => <Row gutter={[16, 16]} {...props} />
export const GridRow = Row
export const GridColumn = ({ width, computer, tablet, mobile, ...props }: any) => {
  const span = width || computer || 24
  return <Col span={span} xs={mobile || 24} sm={tablet || span} lg={computer || span} {...props} />
}
Grid.Row = GridRow
Grid.Column = GridColumn

// Container
export const Container = ({ fluid, text, ...props }: any) => {
  const style = fluid ? {} : { maxWidth: text ? 700 : 1200, margin: '0 auto' }
  return <div style={style} {...props} />
}

// Segment
export const Segment = ({
  basic,
  raised,
  padded,
  compact,
  secondary,
  tertiary,
  inverted,
  color,
  ...props
}: any) => {
  const style: React.CSSProperties = {
    padding: padded === 'very' ? 32 : padded ? 24 : compact ? 8 : 16,
    background: secondary ? '#f5f5f5' : tertiary ? '#fafafa' : inverted ? '#1f1f1f' : '#fff',
    border: basic ? 'none' : '1px solid #d9d9d9',
    borderRadius: 4,
    boxShadow: raised ? '0 2px 8px rgba(0,0,0,0.1)' : undefined,
    ...props.style,
  }

  return <div style={style} {...props} />
}

// Header
export const Header = ({ as = 'h1', size, subheader, icon, content, children, ...props }: any) => {
  const Component =
    Typography[
      as === 'h1'
        ? 'Title'
        : as === 'h2'
          ? 'Title'
          : as === 'h3'
            ? 'Title'
            : as === 'h4'
              ? 'Title'
              : 'Text'
    ]
  const level = as === 'h1' ? 1 : as === 'h2' ? 2 : as === 'h3' ? 3 : as === 'h4' ? 4 : 5

  return (
    <Component level={level} {...props}>
      {icon && <Icon name={icon} />}
      {content || children}
      {subheader && (
        <Typography.Text type="secondary" style={{ display: 'block', fontSize: '0.9em' }}>
          {subheader}
        </Typography.Text>
      )}
    </Component>
  )
}

// Modal
export const Modal = ({
  open,
  onClose,
  size,
  closeOnDimmerClick = true,
  closeOnEscape = true,
  children,
  trigger,
  ...props
}: any) => {
  const [visible, setVisible] = React.useState(false)

  React.useEffect(() => {
    if (open !== undefined) {
      setVisible(open)
    }
  }, [open])

  const handleClose = () => {
    setVisible(false)
    onClose?.()
  }

  if (trigger) {
    return (
      <>
        {React.cloneElement(trigger, { onClick: () => setVisible(true) })}
        <AntModal
          open={visible}
          onCancel={handleClose}
          footer={null}
          width={
            size === 'tiny'
              ? 400
              : size === 'small'
                ? 600
                : size === 'large'
                  ? 900
                  : size === 'fullscreen'
                    ? '100%'
                    : undefined
          }
          {...props}
        >
          {children}
        </AntModal>
      </>
    )
  }

  return (
    <AntModal
      open={visible}
      onCancel={handleClose}
      footer={null}
      maskClosable={closeOnDimmerClick}
      keyboard={closeOnEscape}
      width={
        size === 'tiny'
          ? 400
          : size === 'small'
            ? 600
            : size === 'large'
              ? 900
              : size === 'fullscreen'
                ? '100%'
                : undefined
      }
      {...props}
    >
      {children}
    </AntModal>
  )
}

Modal.Header = ({ children, ...props }: any) => (
  <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 16 }} {...props}>
    {children}
  </div>
)
Modal.Content = ({ children, image, scrolling, ...props }: any) => (
  <div
    style={{ maxHeight: scrolling ? 400 : undefined, overflow: scrolling ? 'auto' : undefined }}
    {...props}
  >
    {children}
  </div>
)
Modal.Description = ({ children, ...props }: any) => <div {...props}>{children}</div>
Modal.Actions = ({ children, ...props }: any) => (
  <div style={{ marginTop: 24, textAlign: 'right' }} {...props}>
    <Space>{children}</Space>
  </div>
)

// Dropdown
export const Dropdown = ({
  text,
  options = [],
  selection,
  fluid,
  search,
  multiple,
  placeholder,
  value,
  onChange,
  trigger,
  children,
  ...props
}: any) => {
  if (trigger || children) {
    const menu = {
      items: options.map((opt: any) => ({
        key: opt.value || opt.key,
        label: opt.text || opt.label,
        onClick: () => onChange?.(null, { value: opt.value || opt.key }),
      })),
    }

    return (
      <AntDropdown menu={menu} {...props}>
        {trigger || children}
      </AntDropdown>
    )
  }

  if (selection) {
    return (
      <AntSelect
        style={{ width: fluid ? '100%' : undefined }}
        placeholder={placeholder || text}
        value={value}
        onChange={(val) => onChange?.(null, { value: val })}
        showSearch={search}
        mode={multiple ? 'multiple' : undefined}
        {...props}
      >
        {options.map((opt: any) => (
          <AntSelect.Option key={opt.value || opt.key} value={opt.value || opt.key}>
            {opt.text || opt.label}
          </AntSelect.Option>
        ))}
      </AntSelect>
    )
  }

  return null
}

// Table
export const Table = ({
  children,
  celled,
  striped,
  basic,
  compact,
  definition,
  fixed,
  selectable,
  singleLine,
  ...props
}: any) => {
  const className = [
    'ui-table',
    celled && 'celled',
    striped && 'striped',
    basic && 'basic',
    compact && 'compact',
    definition && 'definition',
    fixed && 'fixed',
    selectable && 'selectable',
    singleLine && 'single-line',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <table className={className} {...props}>
      {children}
    </table>
  )
}

Table.Header = ({ children, ...props }: any) => <thead {...props}>{children}</thead>
Table.Body = ({ children, ...props }: any) => <tbody {...props}>{children}</tbody>
Table.Footer = ({ children, ...props }: any) => <tfoot {...props}>{children}</tfoot>
Table.Row = ({ children, active, positive, negative, warning, error, ...props }: any) => {
  const className = [
    active && 'active',
    positive && 'positive',
    negative && 'negative',
    warning && 'warning',
    error && 'error',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <tr className={className} {...props}>
      {children}
    </tr>
  )
}
Table.HeaderCell = ({ children, ...props }: any) => <th {...props}>{children}</th>
Table.Cell = ({ children, ...props }: any) => <td {...props}>{children}</td>

// Menu
export const Menu = ({
  items = [],
  vertical,
  tabular,
  pointing,
  secondary,
  fluid,
  activeItem,
  onItemClick,
  children,
  ...props
}: any) => {
  if (children) {
    return (
      <div className="ui-menu" {...props}>
        {children}
      </div>
    )
  }

  const menuItems = items.map((item: any) => ({
    key: item.name || item.key,
    label: item.content || item.label || item.name,
    onClick: () => onItemClick?.(null, { name: item.name || item.key }),
  }))

  return (
    <AntMenu
      mode={vertical ? 'vertical' : 'horizontal'}
      selectedKeys={[activeItem]}
      items={menuItems}
      {...props}
    />
  )
}

Menu.Item = ({ name, active, onClick, children, ...props }: any) => (
  <AntMenu.Item key={name} onClick={() => onClick?.(null, { name })} {...props}>
    {children}
  </AntMenu.Item>
)

// Radio
export const Radio = ({ label, ...props }: any) => <AntRadio {...props}>{label}</AntRadio>

// Checkbox
export const Checkbox = ({ label, ...props }: any) => <AntCheckbox {...props}>{label}</AntCheckbox>

// Label
export const Label = ({
  basic,
  color,
  pointing,
  ribbon,
  circular,
  empty,
  size,
  content,
  children,
  ...props
}: any) => {
  const colorMap: Record<string, string> = {
    red: 'red',
    orange: 'orange',
    yellow: 'gold',
    olive: 'lime',
    green: 'green',
    teal: 'cyan',
    blue: 'blue',
    violet: 'purple',
    purple: 'purple',
    pink: 'magenta',
    brown: 'volcano',
    grey: 'default',
    black: 'default',
  }

  return (
    <AntTag color={colorMap[color] || color} {...props}>
      {content || children}
    </AntTag>
  )
}

// Message
export const Message = ({
  error,
  warning,
  info,
  success,
  positive,
  negative,
  header,
  content,
  children,
  list,
  ...props
}: any) => {
  const type =
    error || negative
      ? 'error'
      : warning
        ? 'warning'
        : info
          ? 'info'
          : success || positive
            ? 'success'
            : 'info'

  return (
    <Alert
      type={type}
      message={header}
      description={
        content ||
        children ||
        (list && (
          <ul>
            {list.map((item: string, i: number) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        ))
      }
      {...props}
    />
  )
}

// Card
const BaseCard = AntCard
const CardWithSubcomponents = BaseCard as typeof BaseCard & {
  Group: React.ComponentType<any>
  Content: React.ComponentType<any>
  Header: React.ComponentType<any>
  Meta: React.ComponentType<any>
  Description: React.ComponentType<any>
}
CardWithSubcomponents.Group = ({ children, ...props }: any) => (
  <Row gutter={[16, 16]} {...props}>
    {children}
  </Row>
)

CardWithSubcomponents.Content = ({ children, ...props }: any) => <div {...props}>{children}</div>
CardWithSubcomponents.Header = ({ children, ...props }: any) => (
  <div style={{ fontWeight: 500, marginBottom: 8 }} {...props}>
    {children}
  </div>
)
CardWithSubcomponents.Meta = ({ children, ...props }: any) => (
  <div style={{ color: '#999', fontSize: 14 }} {...props}>
    {children}
  </div>
)
CardWithSubcomponents.Description = ({ children, ...props }: any) => (
  <div {...props}>{children}</div>
)

export { CardWithSubcomponents as Card }

// Divider
export const Divider = AntDivider

// Image
export const Image = ({ src, alt, fluid, size, ...props }: any) => (
  <img src={src} alt={alt} style={{ width: fluid ? '100%' : size, ...props.style }} {...props} />
)

// Loader
export const Loader = ({ active, inline, size, ...props }: any) => {
  if (inline === 'centered') {
    return (
      <div style={{ textAlign: 'center' }}>
        <Spin size={size} {...props} />
      </div>
    )
  }
  return <Spin size={size} spinning={active} {...props} />
}

// List
export const List = AntList
List.Item = AntList.Item

// Placeholder
export const Placeholder = ({ children, ...props }: any) => <Empty {...props}>{children}</Empty>

// Progress
export const Progress = ({ percent, size, color, active, ...props }: any) => (
  <AntProgress
    percent={percent}
    size={size === 'tiny' ? 'small' : size === 'small' ? 'default' : 'default'}
    status={active ? 'active' : undefined}
    strokeColor={color}
    {...props}
  />
)

// Step
const BaseStep = AntSteps
const StepWithSubcomponents = BaseStep as typeof BaseStep & {
  Group: React.ComponentType<any>
}
StepWithSubcomponents.Group = ({ children, ...props }: any) => (
  <AntSteps {...props}>{children}</AntSteps>
)

export { StepWithSubcomponents as Step }

// Popup
export const Popup = ({ trigger, content, children, ...props }: any) => (
  <AntTooltip title={content} {...props}>
    {trigger || children}
  </AntTooltip>
)

// TextArea
export const TextArea = AntInput.TextArea

// Tab
const BaseTab = ({ panes = [], activeIndex, onTabChange, menu, ...props }: any) => {
  const items = panes.map((pane: any, index: number) => ({
    key: String(index),
    label: pane.menuItem,
    children: pane.render(),
  }))

  return (
    <AntTabs
      activeKey={String(activeIndex || 0)}
      onChange={(key) => onTabChange?.(null, { activeIndex: parseInt(key) })}
      items={items}
      type={menu?.secondary ? 'line' : undefined}
      {...props}
    />
  )
}

const TabWithSubcomponents = BaseTab as typeof BaseTab & {
  Pane: React.ComponentType<any>
}

TabWithSubcomponents.Pane = ({ children, attached, ...props }: any) => (
  <div {...props}>{children}</div>
)

export { TabWithSubcomponents as Tab }

// Select component
export const Select = AntSelect

// Confirm component
export const Confirm = ({ open, onConfirm, onCancel, content, ...props }: any) => {
  AntModal.confirm({
    title: 'Confirm',
    content,
    onOk: onConfirm,
    onCancel,
    ...props,
  })
  return null
}

// Dimmer component
export const Dimmer = ({ active, children, ...props }: any) => {
  if (!active) return children
  return (
    <div style={{ position: 'relative' }}>
      <Spin
        spinning={active}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />
      <div style={{ opacity: 0.5, pointerEvents: 'none' }}>{children}</div>
    </div>
  )
}

// Re-export unchanged components
export {
  Row,
  Col,
  Space,
  Empty,
  Spin,
  Alert,
  Typography,
  message,
  AntAvatar as Avatar,
  AntBadge as Badge,
  AntBreadcrumb as Breadcrumb,
  AntDatePicker as DatePicker,
  AntSwitch as Switch,
  AntUpload as Upload,
  AntPagination as Pagination,
}
